/**
 * src/shell/agents/services/CanvasMutationEngine.ts
 *
 * AST-Based Incremental Canvas Mutator & Diffing Engine.
 * 1. Executes in-place property updates (color schemes, thresholds, filters).
 * 2. Appends new widgets into available grid coordinates.
 * 3. Prunes and compacts widgets without full canvas re-renders.
 * 4. Maintains a 20-step reversible Undo/Redo stack.
 */

import {
  ICanvasDashboardSpec,
  IGenUIWidgetSpec,
  WidgetType,
} from '../types/genui-types';

export interface MutationResult {
  mutatedSpec: ICanvasDashboardSpec;
  operationType: 'PROPERTY_PATCH' | 'ADD_WIDGET' | 'REMOVE_WIDGET' | 'REORGANIZED';
  description: string;
}

export class CanvasMutationEngine {
  private historyStack: ICanvasDashboardSpec[] = [];
  private redoStack: ICanvasDashboardSpec[] = [];
  private readonly maxHistory = 20;

  public mutate(
    currentSpec: ICanvasDashboardSpec,
    instruction: string
  ): MutationResult {
    // Save snapshot for undo
    this.saveSnapshot(currentSpec);

    const lower = instruction.toLowerCase();
    const updatedWidgets: IGenUIWidgetSpec[] = JSON.parse(JSON.stringify(currentSpec.widgets));
    let operationType: MutationResult['operationType'] = 'PROPERTY_PATCH';
    let description = '';

    // 1. Color change patch (e.g., "Make heatmap red / crimson / emerald")
    if (/\bred\b/.test(lower) || lower.includes('crimson')) {
      const target = updatedWidgets.find((w) => w.type === 'access_heatmap' || lower.includes(w.title.toLowerCase()));
      if (target) {
        target.colorScheme = 'crimson';
        description = `Updated ${target.title} color scheme to crimson.`;
      }
    } else if (/\bgreen\b/.test(lower) || lower.includes('emerald')) {
      const target = updatedWidgets.find((w) => w.type === 'access_heatmap' || w.type === 'credit_sankey');
      if (target) {
        target.colorScheme = 'emerald';
        description = `Updated ${target.title} color scheme to emerald.`;
      }
    } else if (/\byellow\b/.test(lower) || lower.includes('amber')) {
      const target = updatedWidgets.find((w) => w.type === 'access_timeline');
      if (target) {
        target.colorScheme = 'amber';
        description = `Updated ${target.title} color scheme to amber.`;
      }
    }

    // 2. Remove widget patch (e.g., "Remove sankey / Delete heatmap")
    else if (lower.includes('remove') || lower.includes('delete') || lower.includes('drop')) {
      operationType = 'REMOVE_WIDGET';
      const initialCount = updatedWidgets.length;
      let targetType: WidgetType | null = null;
      if (lower.includes('sankey') || lower.includes('credit')) targetType = 'credit_sankey';
      if (lower.includes('heat') || lower.includes('density')) targetType = 'access_heatmap';
      if (lower.includes('topology') || lower.includes('mesh')) targetType = 'mesh_topology';
      if (lower.includes('timeline') || lower.includes('event')) targetType = 'access_timeline';

      const filtered = targetType
        ? updatedWidgets.filter((w) => w.type !== targetType)
        : updatedWidgets.slice(0, -1);

      if (filtered.length < initialCount) {
        description = `Pruned ${targetType || 'widget'} from dashboard canvas.`;
      }
      return this.finalizeSpec(currentSpec, filtered, operationType, description);
    }

    // 3. Add widget patch (e.g., "Add mesh topology", "Add credit velocity")
    else if (lower.includes('add') || lower.includes('include') || lower.includes('show')) {
      operationType = 'ADD_WIDGET';
      const maxY = updatedWidgets.reduce((acc, w) => Math.max(acc, w.layout.y + w.layout.h), 0);

      if (lower.includes('topology') || lower.includes('mesh')) {
        updatedWidgets.push({
          id: `widget_topo_mut_${Date.now()}`,
          type: 'mesh_topology',
          title: 'Gossip Mesh Topology Graph',
          layout: { id: `topo_${Date.now()}`, x: 0, y: maxY, w: 12, h: 4 },
          dataSource: { endpoint: 'telemetry:nodes', refreshIntervalMs: 2000 },
          colorScheme: 'cyber',
          config: { renderGlow: true },
        });
        description = 'Added P2P Gossip Mesh Topology card.';
      } else if (lower.includes('sankey') || lower.includes('credit')) {
        updatedWidgets.push({
          id: `widget_sankey_mut_${Date.now()}`,
          type: 'credit_sankey',
          title: 'microCredit Resource Flow',
          layout: { id: `sankey_${Date.now()}`, x: 0, y: maxY, w: 6, h: 4 },
          dataSource: { endpoint: 'ledger:credits', refreshIntervalMs: 3000 },
          colorScheme: 'emerald',
          config: { showFees: true },
        });
        description = 'Added microCredit flow diagram.';
      }
    }

    // 4. Fallback description
    if (!description) {
      description = `Applied refinement: "${instruction}"`;
    }

    return this.finalizeSpec(currentSpec, updatedWidgets, operationType, description);
  }

  public undo(currentSpec: ICanvasDashboardSpec): ICanvasDashboardSpec | null {
    if (this.historyStack.length === 0) return null;
    this.redoStack.push(currentSpec);
    return this.historyStack.pop() || null;
  }

  public redo(currentSpec: ICanvasDashboardSpec): ICanvasDashboardSpec | null {
    if (this.redoStack.length === 0) return null;
    this.historyStack.push(currentSpec);
    return this.redoStack.pop() || null;
  }

  public canUndo(): boolean {
    return this.historyStack.length > 0;
  }

  public canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  private saveSnapshot(spec: ICanvasDashboardSpec): void {
    this.historyStack.push(JSON.parse(JSON.stringify(spec)));
    if (this.historyStack.length > this.maxHistory) {
      this.historyStack.shift();
    }
    this.redoStack = [];
  }

  private finalizeSpec(
    currentSpec: ICanvasDashboardSpec,
    widgets: IGenUIWidgetSpec[],
    operationType: MutationResult['operationType'],
    description: string
  ): MutationResult {
    const mutatedSpec: ICanvasDashboardSpec = {
      ...currentSpec,
      widgets,
      version: currentSpec.version + 1,
      lastMutatedAt: new Date().toISOString(),
    };

    return {
      mutatedSpec,
      operationType,
      description,
    };
  }
}

export const canvasMutationEngine = new CanvasMutationEngine();
